/**
 * Typed client for the FastAPI backend.
 *
 * These interfaces mirror the pydantic response models one-for-one; if the
 * server schema changes, this file is the single place the frontend follows.
 */

import { ApiError, requestJson } from "./httpClient";

export interface ApiMeta {
  readonly generated_at: string;
  readonly source: string;
  readonly stale: boolean;
  readonly age_seconds: number;
}

export interface Player {
  readonly rank: number;
  readonly username: string;
  readonly display_name: string;
  readonly role: string | null;
  readonly current_rank: string | null;
  readonly peak_rank: string | null;
  readonly peak_rank_label: string;
  readonly tr: number | null;
  readonly pps: number | null;
  readonly apm: number | null;
  readonly vs: number | null;
  readonly games_played: number;
  readonly games_won: number;
  readonly live: boolean;
  /** True when this player has a profile to open. */
  readonly has_profile: boolean;
  readonly has_avatar: boolean;
}

export interface RosterTotals {
  readonly members: number;
  readonly with_live_stats: number;
  readonly top_peak_label: string;
  readonly median_pps: number | null;
}

export interface Roster {
  readonly meta: ApiMeta;
  readonly totals: RosterTotals;
  readonly players: readonly Player[];
}

export interface ClubLink {
  readonly label: string;
  readonly url: string;
  readonly primary: boolean;
}

export interface Club {
  readonly name: string;
  readonly campus: string;
  readonly blurb: string;
  readonly league: { readonly name: string; readonly abbreviation: string };
  readonly meeting: {
    /** Null until the club sets a standing slot; the UI must not invent one. */
    readonly weekday: number | null;
    readonly weekday_name: string | null;
    readonly start_time: string | null;
    readonly timezone: string;
    readonly location_note: string;
    readonly blurb: string;
  };
  readonly links: readonly ClubLink[];
}

export interface ClubEvent {
  readonly id: string;
  readonly title: string;
  readonly kind: string;
  readonly starts_at: string;
  readonly detail: string;
  readonly location_note: string;
}

export interface Events {
  readonly meta: ApiMeta;
  readonly events: readonly ClubEvent[];
}

/**
 * The public endpoints never mutate, so they carry no CSRF token — hence the
 * `null` cookie argument. Everything else about the transport, including how a
 * failure is turned into a readable sentence, is shared with the authenticated
 * clients.
 */
const getJson = <T,>(path: string, signal?: AbortSignal) =>
  requestJson<T>("/api", path, { signal }, null);

export { ApiError };

export const api = {
  club: (signal?: AbortSignal) => getJson<Club>("/club", signal),
  roster: (signal?: AbortSignal) => getJson<Roster>("/roster", signal),
  events: (count = 3, signal?: AbortSignal) => getJson<Events>(`/events?count=${count}`, signal),
};
